import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isPermissionRefused } from '@citibox-panel/api-client';
import { track } from '@citibox-panel/analytics';
import type { AccountTypeClass } from '../lib/accountType';

// The «Bloquear» WRITE. `retry: false` — a sanction is never re-sent on the agent's behalf: a
// silent retry would block an account the agent may already believe they failed to block.
//
// The mutation variable is the reason: a courier account's block carries one, a «Usuario» block
// none — and then the id goes alone, with no `undefined` beside it.
//
// On success the analytics event fires first (the closed account class and nothing else, so
// neither account data nor the reason can ride along), then the account query is invalidated AND
// AWAITED. Returning the promise is what keeps the mutation pending until the refetch lands, and
// here that is load-bearing rather than tidy: the write returns no account, so the repainted ficha
// is the ONLY acknowledgement of the block — `UserLayout` skeletons on `isPending` alone, so
// settling any earlier would show the agent the still-active ficha for an instant, right after
// they confirmed the sanction.
export function useBlockUser(
  userId: string,
  phoneKey: string,
  blockUser: (id: string, reason?: string) => Promise<void>,
  accountClass: AccountTypeClass,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      reason === undefined ? blockUser(userId) : blockUser(userId, reason),
    retry: false,
    onSuccess: () => {
      track('user_blocked', { account_type: accountClass });
      return queryClient.invalidateQueries({
        queryKey: ['phone-search', phoneKey],
      });
    },
    // The refetch-on-error (any failure but a permission refusal), in the idiom of the baja beside
    // it. Fire-and-forget — never awaited, so the failure reaches the agent without waiting on a
    // read — and key-exact with the account: if another agent blocked it first, or the automation
    // anonymized it, the refetched ficha resolves who won.
    onError: (error) => {
      // A permission refusal is the host's to answer; refetching the refused account would only
      // draw a second 403.
      if (isPermissionRefused(error)) return;
      void queryClient.invalidateQueries({
        queryKey: ['phone-search', phoneKey],
      });
    },
  });
}
